import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import type { AuditRow } from '@/lib/types';

export function AuditPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: () => api<AuditRow[]>('/api/devices/audit-log'),
    refetchInterval: 15_000,
  });
  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-ink tracking-tight">Audit log</h1>
        <p className="text-sm text-slate-500 mt-0.5">Every administrative action, attributed to a user and a device.</p>
      </div>
      <Card>
        <CardBody>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
            <EmptyState icon={<ScrollText className="w-6 h-6" />} title="No audit entries yet" />
          ) : (
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-5 py-2 text-left">Time</th>
                    <th className="px-5 py-2 text-left">User</th>
                    <th className="px-5 py-2 text-left">Action</th>
                    <th className="px-5 py-2 text-left">Device</th>
                    <th className="px-5 py-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-2 text-slate-500 tabular-nums">{r.created_at}</td>
                      <td className="px-5 py-2 font-semibold text-ink">{r.username}</td>
                      <td className="px-5 py-2 font-mono text-xs">{r.action}</td>
                      <td className="px-5 py-2 font-mono text-xs text-slate-500">{r.device_client_id || '—'}</td>
                      <td className="px-5 py-2 text-slate-600">{r.details || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
